<template>
  <div class="media-zoom-carousel">
    <div class="media-zoom-carousel__container row flex">
      <ul class="media-zoom-carousel__thumbs m0 p0" ref="thumbs">
        <li class="media-zoom-carousel__thumb bg-cl-secondary" v-for="(images, index) in gallery" :key="images.src">
          <product-image
            @click="navigate(index)"
            :image="images"
            :alt="galleryImageAlt(index)"
          />
        </li>
      </ul>
      <div class="media-zoom-carousel__gallery">
        <ssr-carousel
          ref="zoomCarousel"
          class="media-zoom-carousel__carousel"
          @change="pageChange"
          :show-arrows="true"
          gutter="0"
        >
          <slide
            v-for="(images, index) in gallery"
            :key="images.src"
          >
            <div
              class="media-zoom-carousel__slide bg-cl-secondary"
              :class="{'video-container h-100 flex relative': images.video}"
            >
              <product-image
                unlazy
                v-show="hideImageAtIndex !== index"
                :image="images"
                :alt="galleryImageAlt(index)"
              />
              <product-video
                v-if="images.video && (index === currentPage)"
                v-bind="images.video"
                :index="index"
                @video-started="onVideoStarted"
              />
            </div>
          </slide>
        </ssr-carousel>
      </div>
    </div>
  </div>
</template>

<script>
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { htmlDecode } from '@vue-storefront/core/lib/store/filters'
import ProductImage from './ProductImage'
import ProductVideo from './ProductVideo'

import SsrCarousel from 'vue-ssr-carousel'
import ssrCarouselCss from 'vue-ssr-carousel/index.css'
import { pinchZoom } from 'theme/helpers/pinchzoom'

export default {
  name: 'ProductGalleryZoomCarousel',
  props: {
    currentSlide: {
      type: Number,
      required: false,
      default: 0
    },
    gallery: {
      type: Array,
      required: true
    },
    productName: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      carouselTransitionSpeed: 300,
      currentPage: 0,
      hideImageAtIndex: null
    }
  },
  components: {
    ProductImage,
    ProductVideo,
    'ssr-carousel': SsrCarousel
  },
  mounted () {
    this.$nextTick(() => {
      disableBodyScroll(this.$refs.thumbs)
      pinchZoom(this.$refs.zoomCarousel.$el.querySelector('.media-zoom-carousel__slide'))
    })
    this.navigate(this.currentSlide)
    if (this.$refs.zoomCarousel) {
      const navigation = this.$refs.zoomCarousel.$children.find(c => c.$el.className === 'VueCarousel-navigation')
      const pagination = this.$refs.zoomCarousel.$children.find(c => c.$el.className === 'VueCarousel-pagination')
      if (navigation !== undefined) {
        navigation.$on('navigationclick', this.increaseCarouselTransitionSpeed)
      }
      if (pagination !== undefined) {
        pagination.$on('paginationclick', this.increaseCarouselTransitionSpeed)
      }
    }
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onKeydown)
  },
  destroyed () {
    clearAllBodyScrollLocks()
  },
  methods: {
    galleryImageAlt (index) {
      const name = htmlDecode(this.productName || '')
      const n = typeof index === 'number' ? index + 1 : 1
      return name ? `${name} – Bild ${n}` : `Produktbild ${n}`
    },
    navigate (index) {
      if (this.$refs.zoomCarousel) {
        this.$refs.zoomCarousel.goto(index)
      }
    },
    onKeydown (e) {
      if (e.defaultPrevented) return
      const t = e.target
      const tag = t && t.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return
      if (!this.$refs.zoomCarousel) return
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        this.$refs.zoomCarousel.back()
      } else if (e.key === 'ArrowRight') {
        e.preventDefault()
        this.$refs.zoomCarousel.next()
      }
    },
    increaseCarouselTransitionSpeed () {
      this.carouselTransitionSpeed = 500
    },
    pageChange (data) {
      this.currentPage = data.index
      this.hideImageAtIndex = null

      document.querySelectorAll('.media-zoom-carousel__slide img').forEach((imageElement) => {
        imageElement.style.transform = ''
        imageElement.style.WebkitTransform = ''
        imageElement.style.zIndex = ''
      });
      pinchZoom(document.querySelectorAll('.media-zoom-carousel__slide')[data.index])
    },
    onVideoStarted (index) {
      this.hideImageAtIndex = index
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~theme/css/base/global_vars';
@import '~theme/css/animations/transitions';
.media-zoom-carousel {
  * {
    box-sizing: border-box;
  }

  &__container{
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    margin: auto;
    overflow: hidden;
    padding: 20px;
    height: 750px;
    max-height: 100%;
    justify-content: space-evenly !important;

    @media (max-width: 767px){
      top: 50%;
      bottom: auto;
      height: auto;
      transform: translate3d(0, -50%, 0);
    }
  }

  &__thumbs{
    list-style: none;
    padding-right: 20px;
    width:100%;
    max-width: 140px;
    height: 100%;
    overflow: auto;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }

    @media (max-width: 767px) {
      display: none;
    }
  }

  &__thumb{
    margin-bottom: 20px;
    max-width: 100%;
    cursor: pointer;

    &:last-of-type {
      margin-bottom: 0;
    }

    & > *{
      opacity: .9;
      will-change: opacity;
      transition: .3s opacity $motion-main;

      &:hover{
        opacity: 1;
      }
    }
  }

  &__gallery{
    max-width: 600px;
    height: 100%;
    flex: 1;
    @media (max-width: 767px) {
      height: auto;
    }
  }

  &__carousel {
    height: 100%;
  }

  &__slide{
    height: 100%;
    max-height: 100%;
  }
}
.thumb-video{
  padding-bottom: calc(319% / (568 / 100));
}
.video-container {
  align-items: center;
  justify-content: center;
}
</style>

<style lang="scss">
.media-zoom-carousel {
  .VueCarousel-wrapper,
  .VueCarousel-inner,
  .VueCarousel-slide {
    height: 100%;
  }
}
</style>
